import { CanvasPreviewDrag } from "canvas/canvas/active/drag";
import { TableEdges } from "canvas/edges-map";
import { DiagramElements } from "canvas/elements";
import { EditColumn } from "diagram/model/edit/edit";
import { DiagramProps } from "diagram/state/state";
import * as React from "react";
import { TableWrapper, TableWrapperProps } from "../wrapper/wrapper";
import { TableInvisible } from "./invisible/invisible";
import { TableMenuState } from "./visible/menu/menu";
import { TableVisible } from "./visible/visible";

interface Props extends TableWrapperProps {
    columns: EditColumn[];
    diagramDispatch: DiagramProps["diagramDispatch"];
    previewDrag: CanvasPreviewDrag;
    tableEdges: TableEdges | null;
    setTableMenuPosition: TableMenuState["setTableMenuPosition"];
}

const createObserver = (
    elements: DiagramElements,
    id: number,
    setVisible: React.Dispatch<React.SetStateAction<boolean>>,
    viewportRoot: HTMLDivElement
): (() => void) => {
    const callback: IntersectionObserverCallback = (entries) => {
        entries.forEach((entry) => {
            setVisible(entry.isIntersecting);
        });
    };
    const observer = new IntersectionObserver(callback, {
        threshold: 0,
        root: viewportRoot,
    });
    const table = elements.tables.get(id)?.element ?? null;
    if (table === null) {
        throw Error("Table is null");
    }
    observer.observe(table);
    return () => observer.disconnect();
};

/** Editor table: full UI when intersecting the scroll viewport, placeholder otherwise. */
export const TableActive = (props: Props) => {
    const [visible, setVisible] = React.useState(false);

    const { elements } = props;
    const { id } = props.table;

    React.useEffect(() => {
        let cancelled = false;
        let rafId = 0;
        let disconnect: (() => void) | null = null;
        const maxAttempts = 600;
        let attempts = 0;
        const tryObserve = (): void => {
            if (cancelled) {
                return;
            }
            const tableEl = elements.tables.get(id)?.element ?? null;
            const viewportEl = elements.viewport;
            if (tableEl === null || viewportEl === null) {
                attempts += 1;
                if (attempts > maxAttempts) {
                    return;
                }
                rafId = requestAnimationFrame(tryObserve);
                return;
            }
            try {
                disconnect = createObserver(
                    elements,
                    id,
                    setVisible,
                    viewportEl
                );
            } catch {
                rafId = requestAnimationFrame(tryObserve);
            }
        };
        tryObserve();
        return () => {
            cancelled = true;
            cancelAnimationFrame(rafId);
            disconnect?.();
        };
    }, [elements, id]);

    return (
        <TableWrapper {...props}>
            {visible ? (
                <TableVisible
                    table={props.table}
                    tableEdges={props.tableEdges}
                    setTableMenuPosition={props.setTableMenuPosition}
                    columns={props.columns}
                    diagramDispatch={props.diagramDispatch}
                    previewDrag={props.previewDrag}
                    elements={props.elements}
                />
            ) : (
                <TableInvisible
                    table={props.table}
                    tableEdges={props.tableEdges}
                    elements={props.elements}
                />
            )}
        </TableWrapper>
    );
};
